'use client';

import React, { useEffect, useState } from 'react';
import { useTenantsService, TenantDto, UpdateTenantRequest } from '../api/tenants';
import { PencilIcon, TrashIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { TenantEditModal } from './TenantEditModal';
import { DeleteTenantDialog } from './DeleteTenantDialog';
import { CancelSubscriptionDialog } from './CancelSubscriptionDialog';
import Tooltip from '@/components/common/Tooltip';

const TenantsTable = () => {
  const tenantsApi = useTenantsService();
  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; details?: string } | null>(null);
    const [editingTenant, setEditingTenant] = useState<TenantDto | null>(null);
    const [deletingTenant, setDeletingTenant] = useState<TenantDto | null>(null);
  const [cancelingTenant, setCancelingTenant] = useState<TenantDto | null>(null);
  const [isCanceling, setIsCanceling] = useState(false);

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const data = await tenantsApi.getTenants();
        setTenants(data);
      } catch (err: any) {
        setError(err.message || 'An unknown error occurred');
      }
      setLoading(false);
    };
    fetchTenants();
  }, [tenantsApi]);

    const handleEdit = (tenant: TenantDto) => {
    setEditingTenant(tenant);
  };

        const handleSave = async (data: UpdateTenantRequest) => {
    if (!editingTenant) return;
    try {
      const updated = await tenantsApi.updateTenant(editingTenant.id, data);
      setTenants(tenants.map(t => t.id === updated.id ? { ...t, ...updated } : t));
      setEditingTenant(null);
    } catch (err: any) {
      setError(err.message || 'An unknown error occurred');
    }
  };

  if (loading) return <p>Loading tenants...</p>;
          if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error.message || 'An unexpected error occurred.'}</span>
        {error.details && <span className="block sm:inline"> - {error.details}</span>}
        <button onClick={() => setError(null)} className="absolute top-0 bottom-0 right-0 px-4 py-3">
          <span className="text-2xl">&times;</span>
        </button>
      </div>
    );
  }

  const handleCancelSubscriptionRequest = (tenant: TenantDto) => {
    setCancelingTenant(tenant);
  };

  const handleCancelSubscriptionConfirm = async () => {
    if (!cancelingTenant) return;
    setIsCanceling(true);
    try {
      await tenantsApi.cancelSubscription(cancelingTenant.id);
      setTenants(tenants.map(t => t.id === cancelingTenant.id ? { ...t, subscription_status: 'canceled', stripe_subscription_id: null } : t));
      setCancelingTenant(null);
    } catch (err: any) {
      setError(err.response?.data || { message: err.message || 'An unknown error occurred' });
    } finally {
      setIsCanceling(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!editingTenant) return;
    try {
      await tenantsApi.cancelSubscription(editingTenant.id);
      setTenants(tenants.map(t => t.id === editingTenant.id ? { ...t, subscription_status: 'canceled', stripe_subscription_id: null } : t));
      setEditingTenant(null);
    } catch (err: any) {
      setError(err.message || 'An unknown error occurred');
    }
  };

  const handleDeleteRequest = (tenant: TenantDto) => {
    setDeletingTenant(tenant);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTenant) return;
    try {
      await tenantsApi.deleteTenant(deletingTenant.id);
      setTenants(tenants.filter(t => t.id !== deletingTenant.id));
      setDeletingTenant(null);
    } catch (err: any) {
      setError(err.message || 'An unknown error occurred');
    }
  };

  return (
    <>
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tenants.map(tenant => (
              <tr key={tenant.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{tenant.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tenant.subscription_status}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tenant.users}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(tenant.created_at * 1000).toLocaleDateString()}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex justify-end space-x-4">
                  <Tooltip content="Edit Tenant">
                    <button onClick={() => handleEdit(tenant)} className="text-indigo-600 hover:text-indigo-900">
                      <PencilIcon className="h-5 w-5" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Delete Tenant">
                    <button onClick={() => handleDeleteRequest(tenant)} className="text-red-600 hover:text-red-900">
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </Tooltip>
                  {tenant.stripe_subscription_id && (
                    <Tooltip content="Cancel Stripe Subscription">
                      <button onClick={() => handleCancelSubscriptionRequest(tenant)} className="text-yellow-600 hover:text-yellow-900">
                        <XCircleIcon className="h-5 w-5" />
                      </button>
                    </Tooltip>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TenantEditModal
        isOpen={!!editingTenant}
        onClose={() => setEditingTenant(null)}
        onSave={handleSave}
        tenant={editingTenant}
      />

      {deletingTenant && (
        <DeleteTenantDialog
          open={!!deletingTenant}
          onClose={() => setDeletingTenant(null)}
          onConfirm={handleDeleteConfirm}
          tenantName={deletingTenant.name}
        />
      )}

      {cancelingTenant && (
        <CancelSubscriptionDialog
          open={!!cancelingTenant}
          onClose={() => setCancelingTenant(null)}
          onConfirm={handleCancelSubscriptionConfirm}
          tenantName={cancelingTenant.name}
          loading={isCanceling}
        />
      )}

      {/* The old modal is removed */}
    </>
  );
};

export default TenantsTable;
